import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCog,
    faDownload,
    faEraser,
    faSpinner,
} from "@fortawesome/sharp-light-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";
import { useAtom } from "jotai";
import { useMemo, useState } from "react";
import type { EmissionRecordView } from "../../../../apiClient/generated";
import { useAppSelector, useEmissionRecordsApiClient } from "../../../../hooks";
import { CustomSwitch } from "../../../../ui/CustomSwitch";
import { CoordinatesField } from "../../../../ui/GenericFields";
import { FullscreenModal, Modal } from "../../../../ui/Modals";
import { formatDate, formatDateTime } from "../../../../utils/dates";
import { CompactTableUI, useDataTableV3 } from "../../../DataTable";
import { useActiveFilters } from "../../../DataTable/ClearFiltersButton";
import { filterStateToApiParams } from "../../../DataTable/state";
import {
    AssignedToFilter,
    DataProviderFilterV2,
    DateRangeFilter,
    EventGlobalStatusFilter,
    LocationFilterV2,
    RangeFilter,
} from "../../../DataTable/TableFilters";
import { DataExportStatus } from "../../../dashboards/DataExport";
import { RateWithUncertaintyCell } from "../../../Emissions/Extras";
import {
    EMISSIONS_CONFIG_ID,
    emissionsTableAtom,
    hoverContextAtom,
} from "../state";

const columnHelper = createColumnHelper<EmissionRecordView>();

const configurableColumns = [
    { id: "eventStatus", name: "Status" },
    { id: "detectedRate", name: "Detection rate" },
    { id: "concentration", name: "Concentration" },
    { id: "provider", name: "Provider" },
    { id: "responsible", name: "Assignee" },
    { id: "detectionTimestamp", name: "Detection Date (UTC)" },
    { id: "createdAt", name: "Match Date (UTC)" },
    { id: "eventUpdatedAt", name: "Last Updated" },
    { id: "dataPointCreatedAt", name: "Import Date" },
    { id: "location", name: "Location" },
    { id: "bridgerEquipmentId", name: "Bridger Equipment ID" },
    { id: "bridgerEquipmentType", name: "Bridger Equipment Type" },
    { id: "bridgerEmissionLocationId", name: "Bridger Emission Location ID" },
    { id: "bridgerDetectionId", name: "Bridger Detection ID" },
    { id: "bridgerPersistence", name: "Bridger Persistence" },
];

export const emissionColumns = [
    columnHelper.accessor(
        (row) => ({
            eventStatus: row.mainEvent.eventStatus,
            mainEventStatus: row.mainEvent.mainEventStatus,
            linkedEvents: row.mainEvent.linkedEvents,
        }),
        {
            id: "eventStatus",
            meta: {
                customFilterWidget: EventGlobalStatusFilter,
                sortingKey: "main_event__event_status",
            },
            cell: (info) => {
                const { eventStatus, mainEventStatus, linkedEvents } =
                    info.getValue();
                if (eventStatus === "LINKED") {
                    return (
                        <div className="capitalize">
                            <span className="">Linked</span>{" "}
                            <p className="italic text-xs whitespace-nowrap">
                                (
                                {mainEventStatus
                                    ?.replaceAll("_", " ")
                                    .toLowerCase()}
                                )
                            </p>
                        </div>
                    );
                }
                return (
                    <span className="capitalize whitespace-nowrap">
                        {eventStatus.replaceAll("_", " ").toLowerCase()}
                        {linkedEvents?.length > 0 &&
                            ` (${linkedEvents.length} linked)`}
                    </span>
                );
            },
            header: () => "Status",
        },
    ),
    columnHelper.accessor(
        (row) => {
            return {
                detectedRate: row.dataPoint?.detectedRate,
                detectedRateUncertainty: row.dataPoint?.detectedRateUncertainty,
            };
        },
        {
            id: "detectedRate",
            meta: {
                customFilterWidget: RangeFilter,
                sortingKey: "data_point__detected_rate",
            },
            cell: (info) => {
                const { detectedRate, detectedRateUncertainty } =
                    info.getValue();
                return (
                    <RateWithUncertaintyCell
                        rate={detectedRate ? detectedRate / 1000 : detectedRate}
                        uncertainty={
                            detectedRateUncertainty
                                ? detectedRateUncertainty / 1000
                                : detectedRateUncertainty
                        }
                        unit="kg/h"
                    />
                );
            },
            header: () => "Detection rate",
        },
    ),
    columnHelper.accessor(
        (row) => {
            return {
                concentration: row.dataPoint?.concentration,
                concentrationUncertainty:
                    row.dataPoint?.concentrationUncertainty,
            };
        },
        {
            id: "concentration",
            meta: {
                customFilterWidget: RangeFilter,
                sortingKey: "data_point__concentration",
            },
            cell: (info) => {
                const { concentration, concentrationUncertainty } =
                    info.getValue();
                return (
                    <RateWithUncertaintyCell
                        rate={concentration}
                        uncertainty={concentrationUncertainty}
                        unit="ppm*m"
                    />
                );
            },
            header: () => "Concentration",
        },
    ),
    columnHelper.accessor(
        (row) => ({
            providerName: row.providerName,
            secondaryDataSource: row.dataPoint?.secondaryDataSource,
        }),
        {
            id: "provider",
            cell: (info) => {
                const value = info.getValue();
                return (
                    <span className="line-clamp-1">
                        {`${value.providerName} ${
                            value.secondaryDataSource?.trim() !== ""
                                ? "(" + value.secondaryDataSource + ")"
                                : ""
                        }`}
                    </span>
                );
            },
            header: () => "Provider",
            enableSorting: false,
            meta: {
                customFilterWidget: DataProviderFilterV2,
                filteringKey: "providerWithSource",
            },
        },
    ),
    columnHelper.accessor((row) => row.mainEvent.responsibleName, {
        id: "responsible",
        meta: {
            filteringKey: "assignedTo",
            customFilterWidget: AssignedToFilter,
        },
        cell: (info) => info.getValue(),
        header: () => "Assignee",
        enableColumnFilter: true,
        enableSorting: false,
    }),
    columnHelper.accessor((row) => row.dataPoint?.detectionTimestamp, {
        id: "detectionTimestamp",
        meta: {
            sortingKey: "data_point__detection_timestamp",
            filteringKey: "detectionDateRange",
            customFilterWidget: DateRangeFilter,
            extraFilterProps: {
                disableAllOption: true,
            },
        },
        cell: (info) => formatDateTime(info.renderValue()),
        header: () => "Detection Date (UTC)",
        enableColumnFilter: true,
    }),
    columnHelper.accessor("createdAt", {
        id: "createdAt",
        meta: {
            customFilterWidget: DateRangeFilter,
            sortingKey: "created_at__date",
        },
        cell: (info) => {
            if (!info.getValue()) {
                return "-";
            }
            return formatDate(info.getValue());
        },
        header: () => "Match Date",
    }),
    columnHelper.accessor((row) => row.mainEvent.updatedAt, {
        id: "eventUpdatedAt",
        meta: {
            customFilterWidget: DateRangeFilter,
            sortingKey: "main_event__updated_at",
        },
        cell: (info) => {
            if (!info.getValue()) {
                return "-";
            }
            return formatDateTime(info.getValue());
        },
        header: () => "Last Updated",
        enableColumnFilter: true,
    }),
    columnHelper.accessor((row) => row.dataPoint?.createdAt, {
        id: "dataPointCreatedAt",
        meta: {
            customFilterWidget: DateRangeFilter,
            sortingKey: "data_point__created_at__date",
        },
        cell: (info) => {
            if (!info.getValue()) {
                return "-";
            }
            return formatDate(info.getValue());
        },
        header: () => "Import Date",
        enableColumnFilter: true,
    }),
    columnHelper.accessor((row) => row.dataPoint?.location, {
        id: "location",
        cell: (info) => {
            const value = info.getValue();
            if (!value) {
                return "";
            }
            return (
                <CoordinatesField
                    coordinates={value.coordinates}
                    allowCopy
                    mapLinkParams={{
                        emissionId: info.row.original.id,
                        lat: value.coordinates[1].toString(),
                        lon: value.coordinates[0].toString(),
                    }}
                />
            );
        },
        header: () => "Location",
        enableColumnFilter: true,
        enableSorting: false,
        meta: {
            customFilterWidget: LocationFilterV2,
        },
    }),
    columnHelper.accessor((row) => row.dataPoint?.data?.bridger_equipment_id, {
        id: "bridgerEquipmentId",
        cell: (info) => info.getValue() ?? "-",
        header: () => "Bridger Equipment ID",
        enableColumnFilter: true,
        enableSorting: true,
        meta: {
            filteringKey: "bridgerEquipmentId",
            sortingKey: "data_point__data__bridger_equipment_id",
        },
    }),
    columnHelper.accessor(
        (row) => row.dataPoint?.data?.bridger_equipment_type,
        {
            id: "bridgerEquipmentType",
            cell: (info) => info.getValue() ?? "-",
            header: () => "Bridger Equipment Type",
            enableColumnFilter: true,
            enableSorting: true,
            meta: {
                filteringKey: "bridgerEquipmentType",
                sortingKey: "data_point__data__bridger_equipment_type",
            },
        },
    ),
    columnHelper.accessor(
        (row) => row.dataPoint?.data?.bridger_emission_location_id,
        {
            id: "bridgerEmissionLocationId",
            cell: (info) => info.getValue() ?? "-",
            header: () => "Bridger Emission Location ID",
            enableColumnFilter: true,
            enableSorting: true,
            meta: {
                filteringKey: "bridgerEmissionLocationId",
                sortingKey: "data_point__data__bridger_emission_location_id",
            },
        },
    ),
    columnHelper.accessor((row) => row.dataPoint?.data?.bridger_detection_id, {
        id: "bridgerDetectionId",
        cell: (info) => info.getValue() ?? "-",
        header: () => "Bridger Detection ID",
        enableColumnFilter: true,
        enableSorting: true,
        meta: {
            filteringKey: "bridgerDetectionId",
            sortingKey: "data_point__data__bridger_detection_id",
        },
    }),
    columnHelper.accessor((row) => row.dataPoint?.data?.bridger_persistence, {
        id: "bridgerPersistence",
        cell: (info) => info.getValue() ?? "-",
        header: () => "Bridger Persistence",
        enableColumnFilter: true,
        enableSorting: true,
        meta: {
            filteringKey: "bridgerPersistence",
            sortingKey: "data_point__data__bridger_persistence",
        },
    }),
];

interface EmissionsTableSettingsProps {
    show: boolean;
    onClose: () => void;
    columnVisibility: Record<string, boolean>;
    setColumnVisibility: (visibility: Record<string, boolean>) => void;
}

const EmissionsTableSettings = (props: EmissionsTableSettingsProps) => {
    return (
        <Modal visible={props.show} size="sm" onClose={props.onClose}>
            <h1 className="text-base font-bold mt-1">Table settings</h1>
            <p className="text-sm text-neutral-500">
                Toggle column visibility for the emissions table.
            </p>

            <div className="text-sm mt-6 flex flex-col divide-y max-h-[300px] overflow-auto">
                {configurableColumns.map((c, index) => (
                    <div className="flex items-center gap-4 py-3" key={index}>
                        <CustomSwitch
                            checked={
                                !(c.id in (props.columnVisibility ?? {})) ||
                                props.columnVisibility[c.id]
                            }
                            onChange={(v) => {
                                const newValues = {
                                    ...(props.columnVisibility ?? {}),
                                };
                                newValues[c.id] = v;
                                props.setColumnVisibility(newValues);
                            }}
                            size="xs"
                        />
                        {c.name}
                    </div>
                ))}
            </div>
        </Modal>
    );
};

interface EmissionsTableProps {
    siteId: string;
    onClickEmission: (emission: EmissionRecordView) => void;
}

export const EmissionsTable = (props: EmissionsTableProps) => {
    const apiClient = useEmissionRecordsApiClient();
    const [showTableSettings, setShowTableSettings] = useState(false);
    const [exportRequested, setExportRequested] = useState(false);
    const [exportId, setExportId] = useState<string>(undefined);
    const canExport = useAppSelector(
        (store) => store.auth.permissions.emissionExport,
    );

    const tableId = `site-${props.siteId}-emissions`;
    const tableAtom = useMemo(
        () => emissionsTableAtom(props.siteId),
        [props.siteId],
    );
    // States
    const [tableState, setTableState] = useAtom(tableAtom);

    // Active filters - excluding the date range
    const activeFilters = useActiveFilters({
        columns: emissionColumns,
        filters: tableState.filters,
        ignoreKeys: ["detectionDateRange"],
    });

    // Table fetch function
    const fetchFunction = async (props: any) => {
        return await apiClient.emissionRecordsList(props);
    };

    // Global (page-wide) context hover
    const [sitePageHoverContext, setSitePageHoverContext] =
        useAtom(hoverContextAtom);

    // Use the hook to get table logic
    const { table, dataQuery, dataName } = useDataTableV3<EmissionRecordView>({
        dataName: tableId,
        configId: EMISSIONS_CONFIG_ID,
        columns: emissionColumns,
        extraFilters: {
            relatedSite: props.siteId,
        },
        fetchFunction: fetchFunction,
        sortable: true,
        filterable: true,
    });

    const requestExport = async () => {
        setExportRequested(true);
        try {
            const response =
                await apiClient.emissionRecordsGenerateCsvReportCreate({
                    ...filterStateToApiParams(tableState.filters),
                    relatedSite: props.siteId,
                });
            if (response.status === "no_items") {
                alert("No emission record data found for selected filters.");
            } else if (response.status === "already_in_progress") {
                alert(
                    "You already requested a data export for the given filters.",
                );
            } else {
                setExportId(response.dataDownloadId);
            }
        } catch {
            const errorMsg = "Error while generating report. ";
            alert(errorMsg);
        }
        setExportRequested(false);
    };

    // FIXME: Flex container from table is expanding when you select to view more items.
    // The height should be fixed instead, but I've already spent WAY TOO MUCH time trying
    // to fix this and will leave as is until the touch-ups.
    // Sometimes I hate flex layout.

    return (
        <div className="flex-1 flex min-h-0 overflow-hidden">
            <CompactTableUI<EmissionRecordView>
                table={table}
                dataName={dataName}
                columns={emissionColumns}
                loadingData={dataQuery.isFetching}
                itemCount={dataQuery.data?.count || 0}
                onClickRow={(_id, emission) => props.onClickEmission(emission)}
                onHoverRow={(id, emission) => {
                    if (
                        emission &&
                        sitePageHoverContext?.emissionIds?.[0] !== id
                    ) {
                        setSitePageHoverContext({
                            emissionIds: [emission.id],
                            relatedPlumeImages: [emission.dataPoint.plumeImage],
                        });
                    } else if (!id && sitePageHoverContext) {
                        setSitePageHoverContext(undefined);
                    }
                }}
                highlightRowIds={sitePageHoverContext?.emissionIds}
                showPagination={true}
                extraFooterActions={[
                    ...(canExport
                        ? [
                              {
                                  label: "Download CSV",
                                  onClick: !exportRequested
                                      ? requestExport
                                      : undefined,
                                  buttonContent: !exportRequested ? (
                                      <>
                                          Download CSV
                                          <FontAwesomeIcon icon={faDownload} />
                                      </>
                                  ) : (
                                      <FontAwesomeIcon
                                          icon={faSpinner}
                                          className="animate-spin"
                                      />
                                  ),
                              },
                          ]
                        : []),
                    {
                        label: "Configure table",
                        onClick: () => setShowTableSettings(true),
                        buttonContent: (
                            <>
                                Configure table
                                <FontAwesomeIcon icon={faCog} />
                            </>
                        ),
                    },
                    ...(activeFilters.length > 0
                        ? [
                              {
                                  label: "Clear filters",
                                  onClick: () =>
                                      setTableState((ts) => ({
                                          ...ts,
                                          filters: {
                                              detectionDateRange:
                                                  ts.filters.detectionDateRange,
                                          },
                                      })),
                                  buttonContent: (
                                      <>
                                          Clear filters
                                          <FontAwesomeIcon icon={faEraser} />
                                      </>
                                  ),
                              },
                          ]
                        : []),
                ]}
            />

            <FullscreenModal
                visible={!!exportId}
                onClose={() => setExportId(undefined)}
                title="Export Emission Data as CSV"
            >
                <DataExportStatus
                    exportId={exportId}
                    onClose={() => setExportId(undefined)}
                />
            </FullscreenModal>
            <EmissionsTableSettings
                show={showTableSettings}
                onClose={() => setShowTableSettings(false)}
                columnVisibility={tableState.columnVisibility}
                setColumnVisibility={(v) =>
                    setTableState((ts) => ({
                        ...ts,
                        columnVisibility: v,
                    }))
                }
            />
        </div>
    );
};
